import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Modal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { backState } from '../../../components/BackLink';
import GenericButton, { GenericButtonTypeEnum } from '../../../components/GenericButton';
import GenericTextField from '../../../components/GenericTextField/GenericTextField';
import ContractDrawer, { ContractSection, PriceMode } from '../../Finance/components/Contracts/ContractDrawer';
import { statusText } from '../../Finance/components/Contracts/contractText';
import ChildPaymentsModal from '../../Finance/components/ChildPaymentsModal';
import PaymentPanel from '../../Finance/components/Payments/PaymentPanel';
import ChildBillingHistoryModal from '../../Finance/components/ChildBillingHistoryModal';
import { CommonModalActionsBox, CommonModalContentBox, CommonModalWrapperBox } from '../../../common/styles/Common.styles';
import { MESSAGE_TYPE } from '../../../common/constants/common.constants';
import { createNotification } from '../../../utilities/helper/notification.helper';
import { DEFAULT_CURRENCY, formatMoney } from '../../../utilities/helper/money.helper';
import { formatMonthKey, formatSerbianDate, toLocalDateString } from '../../../utilities/helper/date.helper';
import {
  BillingSourceEnum,
  InvoiceStatusEnum,
  PAYMENT_METHOD_LABELS
} from '../../../common/enums/invoicing.enum';
import { ChildBillingSummaryDto } from '../../../common/interfaces/invoicing.interface';
import { closeChildBillingProfile, getChildBillingSummary } from '../../../api/invoicing.api';
import StatTile from '../../../components/StatTile';
import StatusChip from '../../../components/StatusChip';
import { FINANCE, FINANCE_INVOICES, INVOICE_CREATE } from '../../../common/constants/routes.constants';
import { useTranslation } from 'react-i18next';

import { palette } from '../../../theme';
interface ChildBillingTabProps {
  kindergartenId: string;
  childId: string;
  childName: string;
}

const Card: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, action, children }) => (
  <Box sx={{ border: `1px solid ${palette.line}`, borderRadius: 4, p: 3, flex: 1, minWidth: 0 }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: 16, fontFamily: 'Poppins' }}>{title}</Typography>
      {action}
    </Stack>
    {children}
  </Box>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.75 }}>
    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{label}</Typography>
    <Box sx={{ fontSize: 14, textAlign: 'right' }}>{children}</Box>
  </Stack>
);

/**
 * One child's money, in the place the child is already managed.
 *
 * Everything here used to live under the invoices workspace, split across a debts list, a contracts
 * list and an invoice list that each had to be searched for the same child. The data
 * arrives in one request so the panels cannot contradict each other while they load.
 */
const ChildBillingTab: React.FC<ChildBillingTabProps> = ({ kindergartenId, childId, childName }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // an invoice opened from here comes back here, under the child's name, not to the Invoices list
  const backHere = backState(`${location.pathname}${location.search}`, childName);
  const [summary, setSummary] = useState<ChildBillingSummaryDto | null>(null);
  const [loader, setLoader] = useState(true);
  const [failed, setFailed] = useState(false);
  // the contract and the child's own price are edited on the same panel as on the Contracts page
  const [panel, setPanel] = useState<{ focus?: ContractSection; priceMode?: PriceMode } | null>(null);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [closeFrom, setCloseFrom] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoader(true);
    try {
      const result = await getChildBillingSummary(kindergartenId, childId);
      setSummary(result.results ?? null);
      setFailed(false);
    } catch (error: any) {
      setFailed(true);
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('billing.summaryLoadFailed'));
    } finally {
      setLoader(false);
    }
  }, [kindergartenId, childId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCloseArrangement = async (): Promise<void> => {
    if (!closeFrom) return;
    try {
      await closeChildBillingProfile(kindergartenId, childId, closeFrom);
      createNotification(MESSAGE_TYPE.SUCCESS, t('billing.arrangement.ended'));
      setCloseFrom(null);
      load();
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('billing.arrangement.endFailed'));
    }
  };

  if (loader) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (failed || !summary) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        {t('billing.notAvailableForChild')}
      </Alert>
    );
  }

  const { balance, contract, billing } = summary;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack>
          <Typography sx={{ fontSize: 24, color: 'text.primary', fontFamily: 'Poppins' }}>{t('billing.title')}</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('finance.allAmountsIn', { currency: DEFAULT_CURRENCY })}</Typography>
        </Stack>
        <Stack direction="row" spacing={1.5}>
          {/* recording money is the daily act; a one-off invoice is the exception, since
              a period is normally generated for everyone at once */}
          <GenericButton
            buttonType={GenericButtonTypeEnum.Primary}
            variant="contained"
            label={t('payments.record')}
            onClick={() => setPaying(true)}
          />
          <GenericButton
            buttonType={GenericButtonTypeEnum.Secondary}
            variant="outlined"
            label={t('invoices.create')}
            onClick={() => navigate(`${INVOICE_CREATE}?kindergartenId=${kindergartenId}&childId=${childId}`, backHere)}
          />
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <StatTile label={t('finance.figures.invoiced')} value={balance.invoicedTotal} />
        <StatTile label={t('finance.figures.paid')} value={balance.paidTotal} />
        <StatTile
          label={t('finance.figures.outstanding')}
          value={balance.outstanding}
          tone={balance.outstanding > 0 ? 'owed' : 'neutral'}
          // the overdue count belongs against the figure it qualifies, not in a separate
          // banner underneath it
          footer={
            balance.overdueCount > 0 ? (
              <Typography sx={{ fontSize: 12, color: palette.error.main, mt: 0.5 }}>
                {balance.oldestDueDate
                  ? t('billing.pastDueOldest', { count: balance.overdueCount, date: formatSerbianDate(balance.oldestDueDate) })
                  : t('billing.pastDue', { count: balance.overdueCount })}
              </Typography>
            ) : undefined
          }
        />
        <StatTile label={t('finance.figures.credit')} value={balance.credit} tone={balance.credit > 0 ? 'credit' : 'neutral'} />
      </Stack>

      {/* four zeroes read as a screen that failed to load rather than as a fact */}
      {!balance.invoicedTotal && !balance.paidTotal && !balance.credit && (
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 3, mt: -2 }}>
          {t('billing.notInvoicedNothingOwed')}
        </Typography>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="stretch">
        <Card
          title={t('contracts.title')}
          action={
            <GenericButton buttonType={GenericButtonTypeEnum.Secondary} variant="outlined" label={t('common.edit')} onClick={() => setPanel({})} />
          }
        >
          <Field label={t('common.group')}>{contract.groupName ?? '—'}</Field>
          <Divider />
          <Field label={t('contracts.number')}>{contract.contractNumber ?? '—'}</Field>
          <Divider />
          <Field label={t('contracts.nextInvoiceNumber')}>{contract.nextInvoiceNumber ?? '—'}</Field>
          <Divider />
          <Field label={t('contracts.payer')}>{contract.payerFullName ?? '—'}</Field>
          <Divider />
          <Field label={t('contracts.readyToInvoice')}>
            <Chip
              size="small"
              label={contract.isReadyForInvoicing ? t('contracts.ready') : statusText(t, contract)}
              color={contract.isReadyForInvoicing ? 'success' : 'warning'}
              variant="outlined"
            />
          </Field>
        </Card>

        <Card
          title={t('billing.arrangement.title')}
          action={
            <Stack direction="row" spacing={1}>
              <GenericButton
                buttonType={GenericButtonTypeEnum.Neutral}
                variant="outlined"
                label={t('billing.arrangement.history')}
                onClick={() => setHistoryOpen(true)}
              />
              {billing.profile && (
                <GenericButton
                  buttonType={GenericButtonTypeEnum.Neutral}
                  variant="outlined"
                  label={t('billing.arrangement.end')}
                  onClick={() => setCloseFrom(toLocalDateString(new Date()))}
                />
              )}
              <GenericButton
                buttonType={GenericButtonTypeEnum.Secondary}
                variant="outlined"
                label={billing.profile ? t('common.edit') : t('billing.arrangement.billDifferently')}
                onClick={() => setPanel({ focus: 'price', priceMode: 'own' })}
              />
            </Stack>
          }
        >
          <Field label={t('billing.arrangement.pricedBy')}>
            {billing.source === BillingSourceEnum.CHILD_PROFILE && t('billing.arrangement.ownArrangement')}
            {billing.source === BillingSourceEnum.GROUP_SCHEME && t('billing.arrangement.groupScheme', { name: billing.schemeName })}
            {billing.source === BillingSourceEnum.NONE && <Typography sx={{ fontSize: 14, color: 'warning.main' }}>{t('billing.arrangement.nothingYet')}</Typography>}
          </Field>
          <Divider />
          <Field label={t('billing.arrangement.inForceSince')}>{formatSerbianDate(billing.profile?.validFrom)}</Field>
          <Divider />
          <Field label={t('billing.arrangement.monthlyFixedNet')}>
            {billing.monthlyFixedNet !== null ? formatMoney(billing.monthlyFixedNet, { withCurrency: true }) : '—'}
          </Field>
          <Divider />
          <Field label={t('billing.arrangement.items')}>{billing.profile ? billing.profile.items.length : '—'}</Field>
          {billing.profile?.note && (
            <>
              <Divider />
              <Field label={t('common.note')}>{billing.profile.note}</Field>
            </>
          )}
          {billing.source === BillingSourceEnum.NONE && (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: 2, fontSize: 13 }}>
              {/* a child in no group has no group to give a price list to */}
              {billing.groupName
                ? t('billing.arrangement.nothingPricesChild', { name: billing.groupName })
                : t('contracts.drawer.price.notEnrolled')}
            </Alert>
          )}
        </Card>
      </Stack>

      <Box sx={{ border: `1px solid ${palette.line}`, borderRadius: 4, p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 16, fontFamily: 'Poppins' }}>{t('invoices.title')}</Typography>
          {summary.invoiceCount > summary.invoices.length && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Neutral}
              variant="outlined"
              label={t('invoices.seeAll', { count: summary.invoiceCount })}
              onClick={() => navigate(`${FINANCE_INVOICES}?kindergartenId=${kindergartenId}`)}
            />
          )}
        </Stack>
        {!summary.invoices.length ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            {t('billing.notInvoicedYet')}
          </Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('invoices.columns.number')}</TableCell>
                <TableCell>{t('invoices.columns.period')}</TableCell>
                <TableCell>{t('invoices.columns.dueDate')}</TableCell>
                <TableCell sx={{ textAlign: 'right' }}>{t('invoices.columns.total')}</TableCell>
                <TableCell sx={{ textAlign: 'right' }}>{t('finance.figures.paid')}</TableCell>
                <TableCell sx={{ textAlign: 'right' }}>{t('invoices.columns.remaining')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary.invoices.map(invoice => (
                <TableRow
                  key={invoice.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`${FINANCE}/invoice/${invoice.id}?kindergartenId=${kindergartenId}`, backHere)}
                >
                  <TableCell sx={{ fontWeight: 600 }}>{invoice.invoiceNumber ?? '—'}</TableCell>
                  <TableCell>{formatMonthKey(invoice.periodStart.slice(0, 7))}</TableCell>
                  <TableCell>{formatSerbianDate(invoice.dueDate)}</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>
                    {formatMoney(invoice.totalGross, { withCurrency: true, currency: invoice.currency })}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>{formatMoney(invoice.paidAmount)}</TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: invoice.totalGross - invoice.paidAmount > 0 ? 700 : 400 }}>
                    {formatMoney(Math.max(invoice.totalGross - invoice.paidAmount, 0))}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <StatusChip kind="invoice" status={invoice.status} />
                      {invoice.status === InvoiceStatusEnum.ISSUED && <StatusChip kind="payment" status={invoice.paymentStatus} />}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <Box sx={{ border: `1px solid ${palette.line}`, borderRadius: 4, p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 16, fontFamily: 'Poppins' }}>{t('payments.title')}</Typography>
          {/* the panel records; removing a payment lives with the full history */}
          <GenericButton
            buttonType={GenericButtonTypeEnum.Secondary}
            variant="outlined"
            label={t('payments.manage')}
            onClick={() => setPaymentsOpen(true)}
          />
        </Stack>
        {!summary.payments.length ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            {t('payments.noneForChild')}
          </Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('payments.columns.received')}</TableCell>
                <TableCell sx={{ textAlign: 'right' }}>{t('payments.columns.amount')}</TableCell>
                <TableCell>{t('payments.columns.method')}</TableCell>
                <TableCell>{t('payments.columns.settled')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary.payments.map(payment => (
                <TableRow key={payment.id}>
                  <TableCell>{formatSerbianDate(payment.paidAt)}</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>{formatMoney(payment.amount)}</TableCell>
                  <TableCell>{t(PAYMENT_METHOD_LABELS[payment.method])}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>
                    {payment.allocations.map(allocation => (
                      <Chip
                        key={allocation.invoiceId}
                        size="small"
                        variant="outlined"
                        sx={{ mr: 0.5 }}
                        label={`${allocation.invoiceNumber ?? t('invoices.draftLower')}: ${formatMoney(allocation.amount)}`}
                      />
                    ))}
                    {payment.unallocatedAmount > 0 && (
                      <Chip size="small" color="info" variant="outlined" label={t('billing.creditAmount', { amount: formatMoney(payment.unallocatedAmount) })} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      {panel && (
        <ContractDrawer
          kindergartenId={kindergartenId}
          contract={contract}
          vatApplicable={summary.vatApplicable}
          focus={panel.focus}
          initialPriceMode={panel.priceMode}
          onClose={() => setPanel(null)}
          onSaved={load}
        />
      )}

      {historyOpen && (
        <ChildBillingHistoryModal
          kindergartenId={kindergartenId}
          childId={childId}
          childName={childName}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      {paying && (
        <PaymentPanel
          kindergartenId={kindergartenId}
          childId={childId}
          childName={childName}
          payerName={contract.payerFullName}
          onClose={() => setPaying(false)}
          onRecorded={load}
        />
      )}

      {paymentsOpen && (
        <ChildPaymentsModal
          kindergartenId={kindergartenId}
          childId={childId}
          childName={childName}
          outstanding={balance.outstanding}
          onClose={() => setPaymentsOpen(false)}
          onChanged={load}
        />
      )}

      {closeFrom !== null && (
        <Modal open onClose={() => setCloseFrom(null)}>
          <CommonModalWrapperBox sx={{ width: 520 }}>
            <CommonModalContentBox>
              <Typography variant="h2" sx={{ fontSize: 22, mb: 1 }}>
                {t('billing.arrangement.endTitle')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 3 }}>
                {t('billing.arrangement.endExplanation')}
              </Typography>
              <GenericTextField label={t('billing.arrangement.endsOn')} isDate maxDate={null} value={closeFrom} onChange={e => setCloseFrom(e.target.value)} />
              <CommonModalActionsBox>
                <GenericButton buttonType={GenericButtonTypeEnum.Neutral} variant="outlined" label={t('common.cancel')} onClick={() => setCloseFrom(null)} />
                <GenericButton buttonType={GenericButtonTypeEnum.Primary} variant="contained" label={t('billing.arrangement.end')} onAsyncAction={handleCloseArrangement} />
              </CommonModalActionsBox>
            </CommonModalContentBox>
          </CommonModalWrapperBox>
        </Modal>
      )}
    </Box>
  );
};

export default ChildBillingTab;
